'use client';

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { HiOutlineGlobe } from '@react-icons/all-files/hi/HiOutlineGlobe';
import { HiOutlineViewList } from '@react-icons/all-files/hi/HiOutlineViewList';
import { HiOutlineViewGrid } from '@react-icons/all-files/hi/HiOutlineViewGrid';
import { HiOutlineChat } from '@react-icons/all-files/hi/HiOutlineChat';
import { HiOutlineQrcode } from '@react-icons/all-files/hi/HiOutlineQrcode';
import { trackEvent } from '@/lib/track';
import {
  createBridgeClickHandler,
  loadToolDraft,
  saveToolDraft,
  clearToolDraft,
} from '@/helpers/toolBridge';
import type { IBracketVariant } from './bracketVariants';
import {
  GENERAL_EXAMPLE_NAMES,
  GENERAL_EXAMPLE_TITLE,
  EVENT_LINKS,
  FORMAT_LINKS,
  SIZE_LINKS,
  variantHref,
} from './bracketVariants';
import {
  BRACKET_HANDOVER_PARAMS,
  BRACKET_TOOL_SLUG,
  BRACKET_WIDGET_SLUG,
  draftToPayload,
  IBracketDraft,
} from './bracketBridge';
import {
  BRACKET_SIZES,

  GUTTER,
  HEADER_H,
  IBracketLayout,
  IEntrant,
  MAX_ENTRANTS,
  MIN_ENTRANTS,
  IPageBox,
  MIN_LEGIBLE_PT,
  MM_PER_PT,
  PAGE_MARGIN_MM,
  TBracketFormat,
  TBracketSize,
  TDrawOrder,
  TFillMode,
  TOrientation,
  TPaperId,
  buildLayout,
  buildMirroredLayout,
  type IEventPlan,
  type IMirroredLayout,
  type IMirroredPlan,
  fittedScaleMmPerUnit,
  fontSizeFor,
  pageBox,
  parseEntrantList,
} from './bracketMath';
import {
  PrintStyles,
  StyledBridgePanel,
  StyledButton,
  StyledCheckRow,
  StyledEmptyStage,
  StyledFitWarning,
  StyledFormatGrid,
  StyledGroup,
  StyledSwatchRow,
  StyledInput,
  StyledPanel,
  StyledPrivacyNote,
  StyledRelatedToolCard,
  StyledRelatedToolGrid,
  StyledRelatedTools,
  StyledRestoredNote,
  StyledSegment,
  StyledSegmented,
  StyledSheet,
  StyledStage,
  StyledStudio,
  StyledStudioHead,
  StyledTextarea,
  StyledToolBody,
  StyledToolHero,
  StyledToolbar,
  StyledWidgetCard,
  StyledWidgetGrid,
  StyledWidgetsSection,
  StyledWorkspace,
} from './BracketTool.styles';
import { renderNodeToBlob, saveFile } from '@/helpers/saveImage';
import {
  BRAND_MARK_PATHS,
  BrandMarkControl,
  BrandMarkStrip,
  BrandMarkTool,
  useBrandMark,
} from '@/components/brandMark/brandMark.comp';

interface IWidget {
  name: string;
  slug: string;
  description?: string;
  teaser?: string;
  // `getRecommendedWidgets` projects this by default, so it was always on the
  // wire. Leaving it off the interface is why these cards rendered as bare
  // text while every other free tool shows the widget's logo.
  logo?: string;
}

/**
 * Formats this tool can print that the Bracket widget cannot run. Kept beside
 * the handover so the two are read together; the widget's own list lives in
 * `TBracketType` in CommonNinja/apps.
 */
const HANDOVER_UNSUPPORTED_FORMATS: TBracketFormat[] = ['consolation', 'triple'];

const TOOL_ICONS: Record<string, React.ReactNode> = {
  'tier-list-maker': <HiOutlineViewList />,
  'alignment-chart-maker': <HiOutlineViewGrid />,
  'quote-card-maker': <HiOutlineChat />,
  'qr-code-generator': <HiOutlineQrcode />,
};

interface IRelatedTool {
  slug: string;
  name: string;
  description: string;
  href: string;
}

interface IProps {
  recommendedWidgets: IWidget[];
  relatedTools: IRelatedTool[];
  /**
   * Which sheet this page opens on. Absent on the general tool, which keeps the
   * original defaults. Every control stays available on every page, so landing
   * on the 8-team sheet never traps anybody at eight.
   */
  variant?: IBracketVariant;
}

/**
 * A worked example, on screen from the first paint.
 *
 * Both the Tier List and Alignment Chart shipped opening on an empty board, and
 * three colleagues could not tell what to do with either. The cause was found
 * in the code: an empty state gives drag-and-drop no object to be discoverable
 * on. This tool opens on a real eight-player draw for the same reason.
 *
 * It is never saved as a draft until the visitor actually edits something, so
 * nobody gets a false "we restored your bracket" note on a first visit.
 */
const EXAMPLE_TITLE = GENERAL_EXAMPLE_TITLE;
const EXAMPLE_NAMES = GENERAL_EXAMPLE_NAMES;

const PRINT_THEMES = [
  { id: 'classic', label: 'Classic', line: '#1f2430', text: '#1f2430', accent: '#1f2430' },
  { id: 'bold', label: 'Bold', line: '#000000', text: '#000000', accent: '#000000' },
  { id: 'soft', label: 'Soft', line: '#6b7280', text: '#374151', accent: '#6b7280' },
  { id: 'blue', label: 'Blue', line: '#1d4ed8', text: '#1e293b', accent: '#1d4ed8' },
];

/** One sheet in the list. The page you are on is a label, not a link. */
const SheetLink = ({
  item,
  current,
}: {
  item: { slug: string; label: string };
  current: boolean;
}) => (
  <li>
    {current ? (
      <span className="current" aria-current="page">
        {item.label}
      </span>
    ) : (
      <Link href={variantHref(item.slug)}>{item.label}</Link>
    )}
  </li>
);

/** Which corner the logo sits in. Corners only: the title owns the top centre. */
type TLogoCorner = 'tl' | 'tr' | 'bl' | 'br';

/**
 * Sheet backgrounds.
 *
 * Deliberately pale. A printable bracket is a thing you write on with a pen,
 * and the darker of these already costs real ink across a full sheet, so
 * anything stronger would be a trap rather than a choice.
 */
const SHEET_BACKGROUNDS: { id: string; label: string }[] = [
  { id: '#ffffff', label: 'White' },
  { id: '#f7f5ef', label: 'Paper' },
  { id: '#eef4fb', label: 'Ice' },
  { id: '#f2f7ee', label: 'Mint' },
  { id: '#fdf3f0', label: 'Blush' },
  { id: '#f4f1fa', label: 'Lilac' },
  { id: '#1f2430', label: 'Ink' },
];

/**
 * What the sheet opens on, and therefore what counts as "the visitor did not
 * choose a background". Only a deliberate choice crosses the bridge, so that a
 * handover does not paint every widget white. See `background` on the payload.
 */
const DEFAULT_SHEET_BACKGROUND = '#ffffff';

/**
 * Rough perceived brightness of a hex colour, 0 (black) to 1 (white).
 *
 * Only used to answer one question: is this background dark enough that black
 * lines would vanish into it? The Ink swatch is there because somebody will
 * want it, and a bracket drawn in black on near-black is not a choice, it is a
 * broken sheet, so the drawing flips to white instead of trusting the visitor
 * to also change the print style.
 */
function brightness(hex: string): number {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return 1;
  const n = parseInt(m[1], 16);
  const r = (n >> 16) & 255;
  const g = (n >> 8) & 255;
  const b = n & 255;
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255;
}

/** 6 MB of logo is already far more than a sheet of paper can show. */
const MAX_UPLOAD_BYTES = 6 * 1024 * 1024;

/**
 * Reads a picked file into a data URL and measures it.
 *
 * Measuring matters: the logo is drawn into the SVG at a width taken from the
 * page, so without the real aspect ratio a wide logo would either be squashed
 * or would have to sit in a letterboxed box with air around it.
 */
function readImage(
  file: File
): Promise<{ dataUrl: string; aspect: number } | null> {
  return new Promise((resolve) => {
    if (!file.type.startsWith('image/') || file.size > MAX_UPLOAD_BYTES) {
      resolve(null);
      return;
    }
    const reader = new FileReader();
    reader.onerror = () => resolve(null);
    reader.onload = () => {
      const dataUrl = String(reader.result || '');
      const img = new window.Image();
      img.onerror = () => resolve(null);
      img.onload = () =>
        resolve({
          dataUrl,
          aspect: img.naturalHeight ? img.naturalWidth / img.naturalHeight : 1,
        });
      img.src = dataUrl;
    };
    reader.readAsDataURL(file);
  });
}

function makeEntrants(names: string[]): IEntrant[] {
  return names.map((name, i) => ({ id: `e${i}-${name}`, name }));
}

export const BracketTool = ({
  recommendedWidgets,
  relatedTools,
  variant,
}: IProps) => {
  const [title, setTitle] = useState(
    variant ? variant.exampleTitle : EXAMPLE_TITLE
  );
  const [raw, setRaw] = useState(
    (variant ? variant.exampleNames : EXAMPLE_NAMES).join('\n')
  );
  const [format, setFormat] = useState<TBracketFormat>(
    variant?.defaultFormat ?? 'single'
  );
  const [order, setOrder] = useState<TDrawOrder>('index');
  /**
   * ⚠️ There is no "Auto" any more. It was removed on 2026-09-16 because it made
   * the size row lie: with the eight-name example loaded, Auto was selected AND
   * 4 was greyed out, so the row showed five numbers of which the one people
   * reached for first could not be clicked and nothing explained why. The size
   * is now always an explicit number, and every number is always clickable.
   */
  const [sizeChoice, setSizeChoice] = useState<TBracketSize>(
    variant?.defaultSize ?? 8
  );
  const [thirdPlace, setThirdPlace] = useState(false);
  /**
   * ⚠️ ON by default on an event sheet, off everywhere else.
   *
   * The default is off so that a half-typed list does not print "bye" against
   * lines the visitor simply has not reached yet. An event plan has no such
   * ambiguity: the two NFL byes are the format, they are known before anybody
   * plays, and an unlabelled blank line beside the 1 seed reads as a line you
   * are supposed to write a team on.
   */
  const [markByes, setMarkByes] = useState(!!variant?.eventPlan);
  const [theme, setTheme] = useState('classic');
  const [fill, setFill] = useState<TFillMode>(variant?.defaultFill ?? 'names');
  /**
   * ⚠️ Pinned, not chosen. Sergei's call 2026-09-16: the paper row was four
   * toggles and a readout for a decision almost nobody makes, on a tool whose
   * whole promise is "press print and it fits".
   *
   * These stay as named constants rather than being inlined, because the print
   * contract is built on them: `@page` pins this exact paper, `.bracket-page`
   * is sized from it in millimetres, and the drawing is fitted inside. A4
   * landscape is what the sheet was verified against, page by page, against
   * real PDF dimensions.
   *
   * ⚠️ Open question worth a decision: the demand for these pages is US, and US
   * paper is Letter (279 x 216 mm), which is NARROWER than A4 landscape
   * (297 x 210 mm). Pinning A4 means a US printer scales the sheet down to fit.
   * Bringing the Letter option back, or detecting the locale, is the fix if
   * that turns out to matter.
   */
  const paper: TPaperId = 'a4';
  const orientation: TOrientation = 'landscape';
  /**
   * The sheet's own background, drawn INSIDE the SVG rather than as a CSS
   * background on the element.
   *
   * That is the whole trick and it is worth stating: a CSS background is the
   * one thing browsers drop from a print by default, and getting it back needs
   * `print-color-adjust: exact`, which Safari has historically ignored. A
   * `<rect>` is content, not decoration, so it prints everywhere and it lands
   * in the PNG export for free.
   */
  const [bg, setBg] = useState<string>(DEFAULT_SHEET_BACKGROUND);
  const [bgImage, setBgImage] = useState<string | null>(null);
  const [bgStrength, setBgStrength] = useState<0.12 | 0.3 | 1>(0.12);

  /*
   * ⚠️ The logo controls were removed on 2026-09-16 ("remove the logo
   * completely too, for now. If needs be, we'll add it later").
   *
   * The RENDERER still accepts a logo: `BracketPages` keeps its optional
   * `logo`, `logoAspect`, `logoCorner` and `logoScale` props, and the corner
   * arithmetic and the millimetre sizing are all still there and still correct.
   * Only the panel that fed them is gone, so bringing it back is a UI change
   * rather than a re-implementation. The upload plumbing, `readImage`, is
   * shared with the background picker and stays in use either way.
   */

  /**
   * Only Players is open on arrival. Everything else folds, and each header
   * carries its own current value so a closed group still reports its state.
   */
  const [openGroups, setOpenGroups] = useState<string[]>(['players']);
  const isOpen = useCallback((id: string) => openGroups.includes(id), [openGroups]);
  const toggleGroup = useCallback(
    (id: string) =>
      setOpenGroups((g) => (g.includes(id) ? g.filter((x) => x !== id) : [...g, id])),
    []
  );

  const [isExample, setIsExample] = useState(true);
  const [restored, setRestored] = useState(false);
  const [drawNonce, setDrawNonce] = useState(0);

  const sheetRef = useRef<HTMLDivElement>(null);

  /*
   * The brand credit. Every bracket page prints its OWN address, so the key is
   * the variant's slug when it has one in BRAND_MARK_PATHS, else the general
   * tool. It sits under the last page inside the sheet, so the preview and the
   * PNG match. It is left off the printed copy (see PrintStyles): each printed
   * page is sized to exactly the printable area, and one extra line would push
   * a blank-ish second sheet out of the printer.
   */
  const markTool: BrandMarkTool =
    variant && variant.slug in BRAND_MARK_PATHS
      ? (variant.slug as BrandMarkTool)
      : 'printable-bracket-maker';
  const mark = useBrandMark(markTool);
  const fitRef = useRef<{ pageCount: number } | null>(null);

  /**
   * The print copy.
   *
   * Printing has to remove the rest of the site from LAYOUT, not just hide it:
   * `visibility: hidden` keeps the space, which is why the first version printed
   * every bracket on 19 pages with 18 blank. The tidy way to express "hide
   * everything else" is one child selector, which needs the sheet to be a direct
   * child of `body`.
   *
   * `createPortal` is the obvious way to do that. This does not use it, and the
   * reason is worth stating accurately because the first version of this comment
   * got it wrong.
   *
   * ⚠️ `next build` in this repo intermittently dies with
   * `Cannot find module for page: /_document` while collecting page data. It
   * failed three times and passed six, with no source change between some of
   * those runs, so it is a FLAKE in the build rather than anything to do with
   * portals. An early A/B blamed `createPortal` on a single pass; three clean
   * builds afterwards disproved that. If you hit it, just build again.
   *
   * Cloning is kept anyway because it is genuinely better here: it costs no
   * second React render, the clone is plain DOM that React can never be
   * surprised by, it exists only for the duration of the print dialog, and
   * hanging it off `beforeprint` means Ctrl+P and the browser menu get the same
   * treatment as our own button.
   */
  const PRINT_ROOT_ID = 'bracket-print-root';

  const mountPrintCopy = useCallback(() => {
    const source = sheetRef.current;
    if (!source) return;

    document.getElementById(PRINT_ROOT_ID)?.remove();

    const root = document.createElement('div');
    root.id = PRINT_ROOT_ID;
    root.setAttribute('aria-hidden', 'true');

    const clone = source.cloneNode(true) as HTMLElement;
    // Two nodes must never share an id, and the print rules key off the root.
    clone.removeAttribute('id');
    root.appendChild(clone);
    document.body.appendChild(root);
  }, []);

  const unmountPrintCopy = useCallback(() => {
    document.getElementById(PRINT_ROOT_ID)?.remove();
  }, []);

  // Covers Ctrl+P and the browser menu, not just our own button.
  useEffect(() => {
    window.addEventListener('beforeprint', mountPrintCopy);
    window.addEventListener('afterprint', unmountPrintCopy);
    return () => {
      window.removeEventListener('beforeprint', mountPrintCopy);
      window.removeEventListener('afterprint', unmountPrintCopy);
      unmountPrintCopy();
    };
  }, [mountPrintCopy, unmountPrintCopy]);

  const names = useMemo(() => parseEntrantList(raw), [raw]);
  const entrants = useMemo(() => makeEntrants(names), [names]);
  const size: TBracketSize = sizeChoice;

  /**
   * More names than slots is now allowed, and says so.
   *
   * The old rule disabled any size smaller than the list, which is why "4 does
   * not work": the example seeds eight names, so 4 was permanently dead. Nobody
   * reads a greyed-out button as "your list is too long", they read it as
   * broken. The sheet now takes the first `size` names and the panel says
   * exactly how many are not on it.
   */
  /**
   * How many names this sheet can actually take.
   *
   * ⚠️ NOT the same as the slot count when there is an event plan. The NFL
   * sheet is a bracket of 16 that holds 14, because two of its lines are byes.
   * Counting capacity as `size` there let a 15th and 16th name be typed, be
   * counted as on the sheet, and then never appear on it: the exact silent-drop
   * this project already hit once, when the tool capped at 32 on a 64 sheet.
   */
  const capacity = useMemo(() => {
    if (variant?.mirroredPlan) return variant.mirroredPlan.regionOrder.length * 4;
    if (variant?.eventPlan)
      return variant.eventPlan.slotPlan.filter((i) => i !== null).length;
    return size;
  }, [variant, size]);

  /**
   * How many of the lines are byes, for the copy that has to count them.
   *
   * The NFL sheet has two, the College Football Playoff sheet has four, and the
   * number is a fact about the format rather than about the visitor's typing,
   * so it is read off the plan and never off what is in the box. A mirrored
   * sheet has none: every one of its lines is a team.
   */
  const byeCount = useMemo(
    () =>
      variant?.eventPlan
        ? variant.eventPlan.slotPlan.filter((i) => i === null).length
        : 0,
    [variant]
  );

  /**
   * Both event shapes settle the draw themselves, so the order control has
   * nothing to decide and "use a bigger sheet" is not advice anybody can act on.
   */
  const fixedDraw = !!variant?.eventPlan || !!variant?.mirroredPlan;
  const placed = useMemo(() => entrants.slice(0, capacity), [entrants, capacity]);
  const overflow = Math.max(0, entrants.length - capacity);

  /**
   * On a size page the size control is hidden, so "pick a bigger size" is not
   * advice anybody can act on. Send them to the sheet that does fit instead.
   */
  const biggerSheet = useMemo(() => {
    if (!overflow || fixedDraw) return null;
    const next = BRACKET_SIZES.find((s) => s >= entrants.length);
    if (!next || next === size || next === 4) return null;
    return { size: next, href: variantHref(`${next}-team-bracket`) };
  }, [overflow, entrants.length, size, variant]);

  // A blank or seeds-only sheet needs no entrants: that IS the product for
  // somebody who wants to write the names in by hand.
  const hasEnough = fill !== 'names' || entrants.length >= MIN_ENTRANTS;

  const box: IPageBox = useMemo(
    () => pageBox(paper, orientation),
    [paper, orientation]
  );
  const pageAspectRef = box.printableWidthMm / box.printableHeightMm;

  const layout: IBracketLayout | null = useMemo(() => {
    if (!hasEnough) return null;
    // drawNonce is read so a re-draw re-runs a random order. It has no other use.
    void drawNonce;
    return buildLayout({
      entrants: placed,
      size,
      order,
      format,
      thirdPlace,
      fill,
      plan: variant?.eventPlan,
      // The paper decides how wide the columns get, so the drawing fills the
      // sheet instead of letterboxing.
      pageAspect: pageAspectRef,
    });
  }, [
    placed,
    size,
    order,
    format,
    thirdPlace,
    fill,
    hasEnough,
    drawNonce,
    pageAspectRef,
    variant,
  ]);

  /**
   * The four-region sheet, when this page is one.
   *
   * Computed alongside `layout` rather than instead of it: the straight tree is
   * pure arithmetic and costs nothing, and leaving it in place means every
   * shared read of it (the draft, the handover payload, the entrant counting)
   * keeps working untouched. Only the drawing changes.
   */
  const mirrored: IMirroredLayout | null = useMemo(() => {
    if (!variant?.mirroredPlan || !hasEnough) return null;
    return buildMirroredLayout({
      entrants: placed,
      plan: variant.mirroredPlan,
      fill,
      pageAspect: pageAspectRef,
    });
  }, [variant, placed, fill, hasEnough, drawNonce, pageAspectRef]);

  /** Marks the board as the visitor's own, so autosave may begin. */
  const touch = useCallback(() => {
    setIsExample(false);
  }, []);

  // Restore a draft, but never over the top of a visitor who has already typed.
  useEffect(() => {
    const draft = loadToolDraft<IBracketDraft>(BRACKET_TOOL_SLUG);
    if (!draft || !Array.isArray(draft.participants) || !draft.participants.length) {
      return;
    }
    setTitle(draft.title || '');
    setRaw(draft.participants.map((p) => p.name).join('\n'));
    /**
     * ⚠️ The draft deliberately does NOT restore the format any more, and this
     * was the real cause of "the double elimination page has single chosen".
     * Autosave is shared across the whole family under one key, so a draft made
     * on the general maker carried `format: 'single'` onto every other page and
     * quietly overrode its default. The format now belongs to the page, not to
     * the draft, so it cannot be restored into the wrong sheet.
     */
    if (draft.order === 'random' || draft.order === 'seeding' || draft.order === 'index') {
      setOrder(draft.order);
    }
    if (draft.theme) setTheme(draft.theme);
    if (typeof draft.background === 'string' && /^#[0-9a-f]{6}$/i.test(draft.background)) {
      setBg(draft.background);
    }
    setThirdPlace(draft.thirdPlace === true);
    setIsExample(false);
    setRestored(true);
    // Intentionally runs once on mount only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Autosave, but only once the board stopped being our example.
  useEffect(() => {
    if (isExample || !hasEnough) return;
    const draft: IBracketDraft = {
      title,
      participants: entrants.map((e, i) => ({
        name: e.name,
        seed: order === 'seeding' ? i + 1 : null,
      })),
      format,
      size,
      thirdPlace,
      background: bg === DEFAULT_SHEET_BACKGROUND ? null : bg,
      theme,
      order,
    };
    saveToolDraft<IBracketDraft>(BRACKET_TOOL_SLUG, draft);
  }, [isExample, hasEnough, title, entrants, format, size, thirdPlace, theme, order, bg]);

  const themeDef = PRINT_THEMES.find((t) => t.id === theme) ?? PRINT_THEMES[0];


  /**
   * What will actually come out of the printer, worked out before they press
   * anything. The drawing is fitted to the page, so it can never overflow; the
   * only question worth surfacing is whether it is still legible once fitted.
   */
  const fit = useMemo(() => {
    if (mirrored) {
      // One page, always, and measured off the mirrored drawing rather than the
      // straight tree the parent still keeps around.
      const pt =
        (fittedScaleMmPerUnit(
          box,
          mirrored.width + REGION_GUTTER * 2 + PAD * 2,
          TITLE_BAND + HEADER_H + mirrored.height + PAD * 2
        ) *
          fontSizeFor(mirrored.slotPitch)) /
        MM_PER_PT;
      const report = { pageCount: 1, smallestPt: pt, legible: pt >= MIN_LEGIBLE_PT };
      fitRef.current = report;
      return report;
    }

    if (!layout) return null;

    const pageCount = layout.losersRounds.length ? 2 : 1;
    const fontUnits = fontSizeFor(layout.slotPitch);

    const parts: { w: number; h: number }[] = [
      {
        w: (layout.rounds.length + 1) * (layout.colW + GUTTER) + 20,
        h:
          40 +
          HEADER_H +
          (layout.thirdPlaceCentre !== null
            ? layout.thirdPlaceCentre + layout.slotPitch
            : layout.height) +
          20,
      },
    ];

    if (pageCount === 2) {
      parts.push({
        w: layout.losersRounds.length * (layout.colW + GUTTER) + 20,
        h: 40 + 18 + HEADER_H + layout.losersHeight + 20,
      });
    }

    // The tightest page decides the warning.
    const smallestPt = Math.min(
      ...parts.map(
        (part) =>
          (fittedScaleMmPerUnit(box, part.w, part.h) * fontUnits) / MM_PER_PT
      )
    );

    const report = {
      pageCount,
      smallestPt,
      legible: smallestPt >= MIN_LEGIBLE_PT,
    };
    fitRef.current = report;
    return report;
  }, [layout, box, mirrored]);

  const print = useCallback(() => {
    trackEvent('tool_used', {
      tool: BRACKET_TOOL_SLUG,
      action: 'print',
      format,
      size,
      entrants: entrants.length,
      order,
      paper,
      orientation,
      fill,
      pages: fitRef.current?.pageCount ?? 1,
    });
    // Built here too: `beforeprint` is reliable in current browsers, but the
    // button must not depend on it. mountPrintCopy is idempotent.
    mountPrintCopy();
    window.print();
    unmountPrintCopy();
  }, [
    format,
    size,
    entrants.length,
    order,
    paper,
    orientation,
    fill,
    mountPrintCopy,
    unmountPrintCopy,
  ]);

  const downloadPng = useCallback(async () => {
    const node = sheetRef.current;
    if (!node) return;
    try {
      // Bundled dependency, not a runtime CDN import. Loaded on demand so it
      // never costs the page's first paint.
      const blob = await renderNodeToBlob(node, {
        pixelRatio: 2,
        backgroundColor: '#ffffff',
        cacheBust: true,
      });
      const filename = `${(title || 'bracket').replace(/[^a-z0-9]+/gi, '-').toLowerCase()}.png`;
      if ((await saveFile(blob, filename)) === 'cancelled') return;
      trackEvent('tool_used', {
        tool: BRACKET_TOOL_SLUG,
        action: 'download_png',
        format,
        size,
        entrants: entrants.length,
        mark: mark.markOn ? 'on' : 'off',
      });
    } catch (err) {
      // A failed export must never break the page. The print path still works.
      console.log('Bracket PNG export failed', err);
    }
  }, [title, format, size, entrants.length, mark.markOn]);

  const reset = useCallback(() => {
    clearToolDraft(BRACKET_TOOL_SLUG);
    setTitle(variant ? variant.exampleTitle : EXAMPLE_TITLE);
    setRaw((variant ? variant.exampleNames : EXAMPLE_NAMES).join('\n'));
    setFormat(variant?.defaultFormat ?? 'single');
    setOrder('index');
    setSizeChoice(variant?.defaultSize ?? 8);
    setBg(DEFAULT_SHEET_BACKGROUND);
    setBgImage(null);
    setBgStrength(0.12);
    setFill(variant?.defaultFill ?? 'names');
    setThirdPlace(false);
    setMarkByes(false);
    setTheme('classic');
    setIsExample(true);
    setRestored(false);
  }, [variant]);

  /**
   * The handover.
   *
   * The participants go across in FINAL DRAW ORDER, so the widget reproduces
   * the bracket that was just printed rather than re-running the draw.
   */
  const buildPayload = useCallback(
    () =>
      draftToPayload({
        title,
        participants: entrants.map((e, i) => ({
          name: e.name,
          seed: order === 'seeding' ? i + 1 : null,
        })),
        format,
        size,
        thirdPlace,
        // Only a deliberate choice. An untouched sheet is white, and painting
        // every handed-over widget white would throw away its skin.
        background: bg === DEFAULT_SHEET_BACKGROUND ? null : bg,
      }),
    [title, entrants, order, format, size, thirdPlace, bg]
  );

  const addToWebsite = createBridgeClickHandler({
    toolSlug: BRACKET_TOOL_SLUG,
    widgetSlug: BRACKET_WIDGET_SLUG,
    placement: 'panel',
    getPayload: buildPayload,
    extraParams: BRACKET_HANDOVER_PARAMS,
  });

  /*
   * ⚠️ The numbered step rail was removed on 2026-09-16 at Sergei's call, and
   * he was right: it announced a three-step process for what is really one
   * screen, and because the page opens on a worked example, steps one and two
   * were already satisfied before anybody touched anything. A rail that is
   * complete on arrival teaches nothing.
   */


  return (
    <>
      <PrintStyles
        $sheetW={box.sheetWidthMm}
        $sheetH={box.sheetHeightMm}
        $printW={box.printableWidthMm}
        $printH={box.printableHeightMm}
        $margin={PAGE_MARGIN_MM}
      />

      <StyledToolHero>
        <h1>
          {variant ? variant.h1Lead : 'Free'}{' '}
          <span className="highlight">
            {variant ? variant.h1Highlight : 'Printable Bracket Maker'}
          </span>
        </h1>
        <p>
          {variant
            ? variant.heroText
            : 'Type your players and print a clean single elimination bracket with the first round filled in and the rest left blank to fill in by hand. One A4 page, every time. No sign-up, nothing uploaded, works on any printer.'}
        </p>
      </StyledToolHero>

      <StyledToolBody>
        <StyledStudio>
          {restored && (
            <StyledRestoredNote>
              <span>We brought back the bracket you were working on.</span>
              <button type="button" onClick={reset}>
                Start fresh
              </button>
            </StyledRestoredNote>
          )}

          <StyledStudioHead>
            <h2>{variant ? variant.toolHeading : 'Build your bracket'}</h2>
            <p>
              {variant
                ? variant.toolSubhead
                : 'Round one is seeded from your list. Every later round prints as blank lines, so you can write the winners in as the tournament is played.'}
            </p>
          </StyledStudioHead>

          <StyledToolbar>
            <StyledButton type="button" $variant="primary" onClick={print} disabled={!hasEnough}>
              Print bracket
            </StyledButton>
            <StyledButton type="button" onClick={downloadPng} disabled={!hasEnough}>
              Download PNG
            </StyledButton>
            {order === 'random' && (
              <StyledButton
                type="button"
                onClick={() => {
                  touch();
                  setDrawNonce((n) => n + 1);
                }}
                disabled={!hasEnough}
              >
                Re-draw
              </StyledButton>
            )}
            <span className="spacer" />
            <StyledButton type="button" $variant="ghost" onClick={reset}>
              Reset
            </StyledButton>
          </StyledToolbar>
          <BrandMarkControl mark={mark} />

          {/*
            The paper row and the "Fits 1 A4 page exactly" readout are gone.
            What survives is the half that protects somebody: a 64-player draw
            genuinely does print at about 5pt, and that is worth saying BEFORE
            they walk to the printer rather than after.
          */}
          {layout && fit && !fit.legible && (
            <StyledFitWarning>
              This many players prints at about {fit.smallestPt.toFixed(1)}pt, which is small
              but readable. Fewer players, or a bigger sheet, gives you more room to write.
            </StyledFitWarning>
          )}

          <StyledWorkspace>
            <StyledPanel>
              {/*
                Direction A, chosen 2026-09-16.

                The panel used to stack eight control blocks in one column with
                every one of them open, which is what made it read as dense. The
                controls themselves were fine; there were simply too many on
                screen at once for a tool whose job is one sheet of paper.

                Four named groups now, and only Players is open on arrival,
                because typing names is the one thing everybody does. Each group
                shows its current value in the header, so a closed group still
                tells you what it is set to and nothing is hidden, only folded.
              */}

              <StyledGroup>
                <button
                  type="button"
                  className="head"
                  aria-expanded={isOpen('players')}
                  onClick={() => toggleGroup('players')}
                >
                  <span className="title">Players</span>
                  <span className="value">
                    {fill === 'names'
                      ? `${entrants.length} typed`
                      : fill === 'seeds'
                        ? 'Seed numbers'
                        : 'Blank sheet'}
                  </span>
                  <span className="chev" aria-hidden="true" />
                </button>
                <div className="body" hidden={!isOpen('players')}>
                  <div className="field">
                    <label className="field-label" htmlFor="bracket-title">
                      Tournament name
                    </label>
                    <StyledInput
                      id="bracket-title"
                      value={title}
                      maxLength={120}
                      placeholder="Summer Cup"
                      onChange={(e) => {
                        touch();
                        setTitle(e.target.value);
                      }}
                    />
                  </div>

                  <div className="field" hidden={!!variant?.lockFill}>
                    <span className="field-label">Fill the sheet with</span>
                    <StyledSegmented>
                      {(
                        [
                          ['names', 'Names'],
                          ['seeds', 'Seed numbers'],
                          ['blank', 'Blank'],
                        ] as [TFillMode, string][]
                      ).map(([id, label]) => (
                        <StyledSegment
                          key={id}
                          type="button"
                          $active={fill === id}
                          onClick={() => {
                            touch();
                            setFill(id);
                          }}
                        >
                          {label}
                        </StyledSegment>
                      ))}
                    </StyledSegmented>
                    <p className="hint">
                      {fill === 'names' &&
                        'Round one is filled in from your list. Later rounds always print blank.'}
                      {fill === 'seeds' &&
                        'Numbered lines with no names, so you can write the draw in by hand.'}
                      {fill === 'blank' &&
                        'A completely empty bracket. Every line prints empty, so you write the whole draw in yourself.'}
                    </p>
                  </div>

                  <div className="field" hidden={fill !== 'names'}>
                    <label className="field-label" htmlFor="bracket-players">
                      Players or teams
                    </label>
                    <StyledTextarea
                      id="bracket-players"
                      value={raw}
                      placeholder={'One name per line\nor separated by commas'}
                      onChange={(e) => {
                        touch();
                        setRaw(e.target.value);
                      }}
                    />
                    <p className="count">
                      <strong>{entrants.length}</strong> of{' '}
                      {fixedDraw ? capacity : MAX_ENTRANTS} &middot; bracket of{' '}
                      {size}
                      {/*
                        ⚠️ The generic wording is left EXACTLY as it was on the
                        nine live sheets. Only the event page gets the new
                        phrasing, because only there is "empty line" wrong: its
                        spare lines are byes, which are part of the format and
                        not a list somebody has yet to finish typing.
                      */}
                      {!fixedDraw &&
                        entrants.length < capacity &&
                        ` · ${capacity - entrants.length} empty line${capacity - entrants.length === 1 ? '' : 's'}`}
                      {variant?.eventPlan &&
                        (entrants.length < capacity
                          ? ` · ${capacity - entrants.length} still to fill in`
                          : ` · ${byeCount === 2 ? 'both' : `all ${byeCount}`} byes drawn in`)}
                      {/*
                        ⚠️ The mirrored sheet gets its own line and must NOT
                        reuse the NFL wording above. There are no byes in a
                        64-team field, so "both byes drawn in" would be a plain
                        falsehood, and an unfilled line here really is a line
                        somebody has not typed yet.
                      */}
                      {variant?.mirroredPlan &&
                        (entrants.length < capacity
                          ? ` · ${capacity - entrants.length} still to fill in`
                          : ' · all four regions filled')}
                    </p>
                    {overflow > 0 && (
                      <p className="warn">
                        {overflow} name{overflow === 1 ? ' is' : 's are'} not on the sheet.{' '}
                        {variant?.mirroredPlan
                          ? `This sheet holds ${capacity}, ${capacity / 4} in each region.`
                          : variant?.eventPlan
                            ? `This sheet holds ${capacity}${
                                variant.eventPlan.halfNoun
                                  ? `, ${capacity / 2} in each ${variant.eventPlan.halfNoun}`
                                  : ''
                              }.`
                            : `A bracket of ${size} holds ${size}.`}{' '}
                        {!variant?.lockSize && size < 64 ? (
                          'Pick a bigger size below to fit everybody.'
                        ) : biggerSheet ? (
                          <>
                            <Link href={biggerSheet.href}>
                              Use the {biggerSheet.size}-team sheet
                            </Link>{' '}
                            to fit everybody.
                          </>
                        ) : (
                          'Shorten the list to fit.'
                        )}
                      </p>
                    )}
                    <p className="hint">
                      Paste a list straight from a spreadsheet. One name per line.
                    </p>
                  </div>
                </div>
              </StyledGroup>

              <StyledGroup>
                <button
                  type="button"
                  className="head"
                  aria-expanded={isOpen('draw')}
                  onClick={() => toggleGroup('draw')}
                >
                  <span className="title">The draw</span>
                  <span className="value">
                    {variant?.mirroredPlan ? (
                      <>
                        {capacity} teams &middot;{' '}
                        {variant.mirroredPlan.regionLabels.length} regions
                      </>
                    ) : variant?.eventPlan ? (
                      // The plan settles both the size and the order, so the
                      // generic "16 slots, as typed" says nothing true here.
                      <>
                        {capacity} teams &middot; {variant.eventPlan.halfLabels
                          ? variant.eventPlan.halfLabels.join(' and ')
                          : 'fixed draw'}
                      </>
                    ) : (
                      <>
                        {size} slots &middot;{' '}
                        {order === 'index'
                          ? 'as typed'
                          : order === 'random'
                            ? 'random'
                            : 'seeded'}
                      </>
                    )}
                  </span>
                  <span className="chev" aria-hidden="true" />
                </button>
                <div className="body" hidden={!isOpen('draw')}>
                  <div className="field" hidden={!!variant?.lockSize}>
                    <span className="field-label">Bracket size</span>
                    <StyledSegmented>
                      {BRACKET_SIZES.map((n) => (
                        <StyledSegment
                          key={n}
                          type="button"
                          $active={sizeChoice === n}
                          onClick={() => {
                            touch();
                            setSizeChoice(n);
                          }}
                        >
                          {n}
                        </StyledSegment>
                      ))}
                    </StyledSegmented>
                    <p className="hint">
                      Slots on the sheet, not players. Pick the smallest one that holds everybody
                      and the spare lines print empty.
                    </p>
                  </div>

                  {/*
                    ⚠️ Hidden on an event page, not just ignored. The slot map IS
                    the draw, so Random and Seeded have nothing to decide, and a
                    control that visibly does nothing is worse than no control.
                    Same reasoning as `lockFormat`, Sergei 2026-09-16.
                  */}
                  <div className="field" hidden={fixedDraw}>
                    <span className="field-label">Draw order</span>
                    <StyledSegmented>
                      {(
                        [
                          ['index', 'As typed'],
                          ['random', 'Random'],
                          ['seeding', 'Seeded'],
                        ] as [TDrawOrder, string][]
                      ).map(([id, label]) => (
                        <StyledSegment
                          key={id}
                          type="button"
                          $active={order === id}
                          onClick={() => {
                            touch();
                            setOrder(id);
                          }}
                        >
                          {label}
                        </StyledSegment>
                      ))}
                    </StyledSegmented>
                    {order === 'seeding' && (
                      <p className="hint">
                        Your list order is the seeding, so the first name is seed 1. Top seeds get
                        the empty lines and meet as late as possible.
                      </p>
                    )}
                  </div>

                  <div className="field">
                    <StyledCheckRow>
                      <input
                        id="bracket-third-place"
                        type="checkbox"
                        checked={thirdPlace}
                        onChange={(e) => {
                          touch();
                          setThirdPlace(e.target.checked);
                        }}
                      />
                      <span>
                        Third place play-off
                        <span className="sub">Adds one extra blank line under the bracket.</span>
                      </span>
                    </StyledCheckRow>
                    {/*
                      ⚠️ Not rendered at all on the mirrored sheet, and NOT via
                      the `hidden` attribute. A 64-team field has no byes, so
                      the tick box would be a control that visibly does nothing.
                      StyledCheckRow is display:flex, which beats `hidden` on
                      its own: that is why `.field` carries an explicit
                      `.field[hidden]` rule in the styles. Dropping the element
                      cannot be got wrong the same way.
                    */}
                    {!variant?.mirroredPlan && (
                      <StyledCheckRow>
                        <input
                          id="bracket-mark-byes"
                          type="checkbox"
                          checked={markByes}
                          onChange={(e) => {
                            touch();
                            setMarkByes(e.target.checked);
                          }}
                        />
                        <span>
                          Write &ldquo;bye&rdquo; on empty lines
                          <span className="sub">
                            {variant?.eventPlan
                              ? 'On by default here: the byes are part of the format, not a short list.'
                              : 'Off by default, so a half-typed list does not print the word.'}
                          </span>
                        </span>
                      </StyledCheckRow>
                    )}
                  </div>
                </div>
              </StyledGroup>

              <StyledGroup>
                <button
                  type="button"
                  className="head"
                  aria-expanded={isOpen('style')}
                  onClick={() => toggleGroup('style')}
                >
                  <span className="title">Sheet style</span>
                  <span className="value">
                    {themeDef.label}
                    {bgImage ? ' · image' : bg !== '#ffffff' ? ' · tinted' : ''}
                  </span>
                  <span className="chev" aria-hidden="true" />
                </button>
                <div className="body" hidden={!isOpen('style')}>
                  <div className="field">
                    <span className="field-label">Ink</span>
                    <StyledSegmented>
                      {PRINT_THEMES.map((t) => (
                        <StyledSegment
                          key={t.id}
                          type="button"
                          $active={theme === t.id}
                          onClick={() => {
                            touch();
                            setTheme(t.id);
                          }}
                        >
                          {t.label}
                        </StyledSegment>
                      ))}
                    </StyledSegmented>
                  </div>

                  <div className="field">
                    <span className="field-label">Background</span>
                    <StyledSwatchRow>
                      {SHEET_BACKGROUNDS.map((b) => (
                        <button
                          key={b.id}
                          type="button"
                          title={b.label}
                          aria-label={b.label}
                          aria-pressed={!bgImage && bg === b.id}
                          className={!bgImage && bg === b.id ? 'swatch on' : 'swatch'}
                          style={{ background: b.id }}
                          onClick={() => {
                            touch();
                            setBgImage(null);
                            setBg(b.id);
                          }}
                        />
                      ))}
                    </StyledSwatchRow>
                    <div className="row">
                      <label className="file-btn">
                        <input
                          id="bracket-background-file"
                          type="file"
                          accept="image/*"
                          onChange={async (e) => {
                            const file = e.target.files?.[0];
                            e.target.value = '';
                            if (!file) return;
                            const read = await readImage(file);
                            if (!read) return;
                            touch();
                            setBgImage(read.dataUrl);
                          }}
                        />
                        {bgImage ? 'Replace image' : 'Use an image'}
                      </label>
                      {bgImage && (
                        <button
                          type="button"
                          className="link-btn"
                          onClick={() => {
                            touch();
                            setBgImage(null);
                          }}
                        >
                          Remove
                        </button>
                      )}
                    </div>
                    {bgImage && (
                      <>
                        <StyledSegmented>
                          {(
                            [
                              [0.12, 'Faint'],
                              [0.3, 'Medium'],
                              [1, 'Full'],
                            ] as [0.12 | 0.3 | 1, string][]
                          ).map(([v, label]) => (
                            <StyledSegment
                              key={label}
                              type="button"
                              $active={bgStrength === v}
                              onClick={() => {
                                touch();
                                setBgStrength(v);
                              }}
                            >
                              {label}
                            </StyledSegment>
                          ))}
                        </StyledSegmented>
                        <p className="hint">
                          Drawn inside the sheet, so it prints and it lands in the PNG. Faint is
                          the one you can still write on.
                        </p>
                      </>
                    )}
                  </div>
                </div>
              </StyledGroup>

            </StyledPanel>

            <StyledStage>
              {layout ? (
                <StyledSheet
                  id="bracket-print-sheet"
                  ref={sheetRef}
                  $ratio={box.printableWidthMm / box.printableHeightMm}
                >
                  <BracketPages
                    layout={layout}
                    title={title}
                    format={format}
                    line={themeDef.line}
                    text={themeDef.text}
                    accent={themeDef.accent}
                    showSeeds={fixedDraw || order === 'seeding' || fill === 'seeds'}
                    plan={variant?.eventPlan}
                    mirrored={mirrored}
                    mirroredPlan={variant?.mirroredPlan}
                    markByes={markByes}
                    background={bg}
                    backgroundImage={bgImage}
                    backgroundStrength={bgStrength}
                  />
                  {mark.markOn && (
                    <div className="bracket-credit">
                      <BrandMarkStrip tool={markTool} background={bg} />
                    </div>
                  )}
                </StyledSheet>
              ) : (
                <StyledEmptyStage>
                  <div>
                    <strong>Add at least two players</strong>
                    Type or paste names into the box, one per line, and your bracket appears here.
                    Or switch to a blank sheet and write them in by hand.
                  </div>
                </StyledEmptyStage>
              )}
            </StyledStage>
          </StyledWorkspace>
        </StyledStudio>

        <StyledPrivacyNote>
          Everything you type stays in your browser and is drawn on your own device. Nothing is
          uploaded. The PNG carries a small commoninja.com credit, and a free account removes it.
        </StyledPrivacyNote>

        <StyledBridgePanel>
          <div className="bridge-copy">
            <span className="bridge-eyebrow">Now make it pay off</span>
            <h2>Want the bracket to run itself?</h2>
            <p>
              A printed sheet is finished the moment it leaves the printer. Move this draw into the
              Bracket widget instead and winners advance on their own, visitors vote and predict,
              and a live leaderboard ranks everyone&rsquo;s picks. Your players come across, so you
              carry on where you left off.
            </p>
            {/*
              * The widget runs single and double elimination. It has no
              * consolation or triple format, so those two hand over as single
              * elimination. Say so here rather than letting somebody find out
              * after the trip: the players and the draw order DO survive, it
              * is only the extra sheet that does not.
              *
              * Derived from the live `format` rather than hardcoded per page,
              * so it cannot drift if a page's default changes or the widget
              * later gains one of these formats.
              */}
            {HANDOVER_UNSUPPORTED_FORMATS.includes(format) && (
              <p className="bridge-caveat">
                Your players and draw order come across, but the widget runs single and double
                elimination only, so this opens there as single elimination. The printed sheet on
                this page is unaffected.
              </p>
            )}

            {/*
              * The same honesty, for the same reason, one level up.
              *
              * ⚠️ The widget has no idea what a conference or a seeding
              * protocol is. It builds one pool into one tree, so it cannot keep
              * two halves apart or reproduce the byes, and saying nothing here
              * would let somebody discover that only after the trip. The teams
              * and their order DO survive, which is the part that took the
              * typing.
              *
              * Derived from the live variant, so a future event sheet gets this
              * for free and it cannot drift.
              */}
            {!!variant?.eventPlan && (
              <p className="bridge-caveat">
                Your {capacity} teams come across in the order you typed them, but the widget
                {variant.eventPlan?.halfNoun
                  ? ` builds one bracket rather than two ${variant.eventPlan.halfNoun}s, so`
                  : ` builds one plain seeded tree, so the ${byeCount} first-round byes and the fixed pairings do not come with it and`}{' '}
                it opens as a standard single elimination draw. The printed sheet on this page is
                unaffected.
              </p>
            )}
            {!!variant?.mirroredPlan && (
              <p className="bridge-caveat">
                Your {capacity} teams come across in the order you typed them, but the widget
                builds one bracket rather than four regions, so it opens as a standard single
                elimination draw of {capacity} and the regions are not carried over. The printed
                sheet on this page is unaffected.
              </p>
            )}
          </div>
          <div className="bridge-action">
            <button
              className="bridge-cta"
              type="button"
              onClick={addToWebsite}
              disabled={!hasEnough}
            >
              Put this bracket on my website &rarr;
            </button>
            <span className="bridge-note">No credit card. Live in minutes.</span>
          </div>
        </StyledBridgePanel>





        {/*
          Names only. This was a grid of bordered cards carrying a sheet-count
          badge and a sentence of explanation each, which made a ten-item
          signpost heavier than the tool above it. Every one of these pages
          explains itself once you are on it, so the list only has to get you
          there.
        */}
        <StyledFormatGrid>
          <h2>Every bracket sheet</h2>

          <h3>By format</h3>
          <ul className="sheets">
            {FORMAT_LINKS.map((f) => (
              <SheetLink key={f.slug} item={f} current={f.slug === variant?.slug} />
            ))}
          </ul>

          <h3>By size</h3>
          <ul className="sheets">
            {SIZE_LINKS.map((f) => (
              <SheetLink key={f.slug} item={f} current={f.slug === variant?.slug} />
            ))}
          </ul>

          <h3>By event</h3>
          <ul className="sheets">
            {EVENT_LINKS.map((f) => (
              <SheetLink key={f.slug} item={f} current={f.slug === variant?.slug} />
            ))}
          </ul>
        </StyledFormatGrid>

        {recommendedWidgets?.length > 0 && (
          <StyledWidgetsSection>
            <h2>Widgets that pair with a tournament</h2>
            <p className="subtitle">
              A printed sheet settles the draw. These free CommonNinja widgets keep the
              tournament alive on your own site once the first results are in.
            </p>
            <StyledWidgetGrid>
              {recommendedWidgets.slice(0, 4).map((w) => (
                <StyledWidgetCard
                  key={w.slug}
                  href={`/widgets/${w.slug}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <div className="widget-header">
                    {w.logo && (
                      <Image src={w.logo} alt={`${w.name} logo`} width={48} height={48} />
                    )}
                    <h3>{w.name}</h3>
                  </div>
                  {/*
                    * Never cut this to a character count. It used to be
                    * `.slice(0, 110)`, which chopped mid-word and shipped
                    * "...and deliver a c" to the page.
                    */}
                  <p>{w.teaser || w.description || ''}</p>
                  <span className="widget-cta">See the widget &rarr;</span>
                </StyledWidgetCard>
              ))}
            </StyledWidgetGrid>
          </StyledWidgetsSection>
        )}

        <StyledRelatedTools>
          <h2>Explore More Free Tools</h2>
          <p className="subtitle">Other free tools to build and share something in a browser tab.</p>
          <StyledRelatedToolGrid>
            {relatedTools.map((t) => (
              <StyledRelatedToolCard key={t.slug} as={Link} href={t.href}>
                <span className="tool-icon">{TOOL_ICONS[t.slug] || <HiOutlineGlobe />}</span>
                <h3>{t.name}</h3>
                <p>{t.description}</p>
                <span className="tool-cta">Use Tool &rarr;</span>
              </StyledRelatedToolCard>
            ))}
          </StyledRelatedToolGrid>
        </StyledRelatedTools>
      </StyledToolBody>

    </>
  );
};

/**
 * The sheet, as one SVG per printed page.
 *
 * SVG rather than canvas because this is destined for paper: it stays crisp at
 * any print size, it costs no library, and the browser's print dialog turns it
 * into true vector output rather than a bitmap.
 *
 * WHY PAGES, AND NOT ONE TALL DRAWING. The first version drew everything into a
 * single SVG and let the print stylesheet scale it to the page width. Two things
 * were wrong with that. A drawing taller than the page simply ran onto a second
 * sheet, and a 32-player double-elimination draw is TALLER than it is wide, so
 * it could never fit a landscape page at a readable size. Splitting the winners
 * and losers halves onto their own pages means each one is fitted to the paper
 * on its own terms, and each is a full, usable sheet.
 */

/** Everything a page needs to draw itself, measured in SVG user units. */
interface IPagePart {
  key: string;
  heading: string | null;
  width: number;
  height: number;
  render: (offsetY: number) => React.ReactNode;
}

const PAD = 10;
const TITLE_BAND = 40;
/**
 * Side gutters on the mirrored sheet, for the region captions.
 *
 * Module scope because the print-size readout has to measure the same drawing
 * the renderer draws. A gutter counted in one place and not the other is how a
 * sheet ends up promising a font size it does not print at.
 */
const REGION_GUTTER = 26;

/**
 * Groups page two's columns into their sections.
 *
 * Double elimination is one band ("Losers bracket"), consolation is one
 * ("Consolation draw"), and triple is two ("Second chance" then "Third
 * chance"), which is the whole visible difference between the triple sheet and
 * the double one. Returns nothing when there is only one unnamed section, so a
 * single-band sheet does not carry a label that says what its own heading
 * already said.
 */
function sectionBands(
  rounds: { name: string; group?: string }[]
): { label: string; from: number; to: number }[] {
  const bands: { label: string; from: number; to: number }[] = [];
  rounds.forEach((r, i) => {
    const label = r.group || '';
    const last = bands[bands.length - 1];
    if (last && last.label === label) last.to = i;
    else bands.push({ label, from: i, to: i });
  });
  if (bands.length < 2) return [];
  return bands.filter((b) => b.label);
}

export const BracketPages = ({
  layout,
  title,
  format,
  line,
  text,
  accent,
  showSeeds,
  plan = undefined,
  mirrored = null,
  mirroredPlan = undefined,
  markByes = false,
  background = '#ffffff',
  backgroundImage = null,
  backgroundStrength = 0.12,
  logo = null,
  logoAspect = 1,
  logoCorner = 'tl',
  logoScale = 0.13,
}: {
  layout: IBracketLayout;
  title: string;
  format: TBracketFormat;
  line: string;
  text: string;
  accent: string;
  showSeeds: boolean;
  /**
   * A fixed real-world draw. Supplies the column headers and, via `halfLabels`,
   * the two conference captions down the left edge. See IEventPlan.
   */
  plan?: IEventPlan;
  /**
   * The four-region sheet that meets in the middle. When this is set it REPLACES
   * `layout` as what gets drawn: the straight tree is still computed by the
   * parent but nothing on the page comes from it. See IMirroredLayout.
   */
  mirrored?: IMirroredLayout | null;
  mirroredPlan?: IMirroredPlan;
  /** Print the word "bye" on unfilled lines. Off by default: see renderMatch. */
  markByes?: boolean;
  /**
   * Background and logo are drawn as SVG content, never as CSS. See the note on
   * the `bg` state: CSS backgrounds are dropped from prints by default, and
   * `print-color-adjust` is not reliable across browsers. A `<rect>` and an
   * `<image>` are content, so they print and they export.
   */
  background?: string;
  backgroundImage?: string | null;
  backgroundStrength?: number;
  logo?: string | null;
  logoAspect?: number;
  logoCorner?: TLogoCorner;
  logoScale?: number;
}) => {
  const darkSheet = !backgroundImage && brightness(background) < 0.45;
  const ink = darkSheet ? '#ffffff' : line;
  const inkText = darkSheet ? '#ffffff' : text;
  const inkAccent = darkSheet ? '#93c5fd' : accent;
  const inkFaint = darkSheet ? '#9ca3af' : '#9aa1ad';

  const fontSize = fontSizeFor(mirrored ? mirrored.slotPitch : layout.slotPitch);
  const colW = mirrored ? mirrored.colW : layout.colW;

  /**
   * How many characters a name can be before it is cut.
   *
   * The nine live sheets keep the flat 22 they have always had. Only the
   * mirrored sheet derives it, because only it trades column width for print
   * size: at 104 units a 22-character name would run out of its column and
   * across the connectors.
   *
   * ⚠️ Deriving it for EVERY sheet was the first version of this and it is
   * wrong. It would have cut the live 8-team sheet at 19 characters instead of
   * 22, which is arguably more honest (22 characters genuinely overflow a
   * 150-unit column at 13px) but is a change to a working page that has nothing
   * to do with March Madness. If it is worth fixing, it is worth fixing on its
   * own.
   */
  const maxLabelChars = mirrored
    ? Math.max(6, Math.floor((colW - 26) / (fontSize * 0.5)))
    : 22;

  /**
   * One match: two writable lines, the bracket join, and the feed forward.
   *
   * `dir` is 1 for a column that advances to the RIGHT, which is every column
   * on every sheet except the right half of a mirrored one, and -1 for a column
   * that advances to the LEFT. Flipping it moves three things and only three:
   * the vertical that joins the pair, the stub that feeds the next round, and
   * which end of the line the name is written from.
   */
  const renderMatch = (
    colX: number,
    lineYs: [number, number],
    centre: number,
    slots: { label: string; isBye: boolean; seed: number | null }[],
    key: string,
    drawStub: boolean,
    dir: 1 | -1 = 1
  ) => {
    const [topY, botY] = lineYs;
    const joinX = dir === 1 ? colX + colW : colX;
    const stubEndX = joinX + dir * GUTTER;

    return (
      <g key={key}>
        {[topY, botY].map((y, i) => {
          const slot = slots[i];
          const seedInset = showSeeds && slot?.seed ? 22 : 5;
          const labelX = dir === 1 ? colX + seedInset : colX + colW - seedInset;
          const seedX = dir === 1 ? colX + 4 : colX + colW - 4;
          const anchor = dir === 1 ? 'start' : 'end';
          return (
            <g key={i}>
              <line x1={colX} y1={y} x2={colX + colW} y2={y} stroke={ink} strokeWidth={1} />
              {slot?.label && (
                <text
                  x={labelX}
                  y={y - 5}
                  fontSize={fontSize}
                  fill={inkText}
                  textAnchor={anchor}
                  fontFamily="inherit"
                >
                  {slot.label.length > maxLabelChars
                    ? `${slot.label.slice(0, maxLabelChars - 1)}…`
                    : slot.label}
                </text>
              )}
              {/* Not gated on a name: in seeds-only mode the number IS the
                  content, and the line is deliberately left empty to write on. */}
              {showSeeds && !!slot?.seed && (
                <text
                  x={seedX}
                  y={y - 5}
                  fontSize={fontSize - 2}
                  fill={inkAccent}
                  textAnchor={anchor}
                  fontFamily="inherit"
                  fontWeight="700"
                >
                  {slot.seed}
                </text>
              )}
              {/*
                ⚠️ An empty line prints EMPTY by default.
                It used to print the italic word "bye" on any unfilled slot, so
                deleting one name from a list of eight made a line read "bye"
                while you were still typing. Two things were wrong with that: a
                half-edited list is not a draw with byes in it, and the word sat
                on the very line you want to write a name on.
                Marking byes is still a real thing an organiser wants on a
                finished seeded sheet, so it is a tick box now, off by default.
              */}
              {slot?.isBye && markByes && (
                <text
                  x={dir === 1 ? colX + 5 : colX + colW - 5}
                  textAnchor={anchor}
                  y={y - 5}
                  fontSize={fontSize - 1}
                  fill={inkFaint}
                  fontFamily="inherit"
                  fontStyle="italic"
                >
                  bye
                </text>
              )}
            </g>
          );
        })}
        <line x1={joinX} y1={topY} x2={joinX} y2={botY} stroke={ink} strokeWidth={1} />
        {drawStub && (
          <line
            x1={joinX}
            y1={centre}
            x2={stubEndX}
            y2={centre}
            stroke={ink}
            strokeWidth={1}
          />
        )}
      </g>
    );
  };

  const roundHeader = (x: number, label: string, key: string) => (
    <text
      key={key}
      x={x + colW / 2}
      y={-12}
      fontSize={10}
      fontWeight="700"
      fill={inkAccent}
      textAnchor="middle"
      letterSpacing="0.06em"
      fontFamily="inherit"
    >
      {label.toUpperCase()}
    </text>
  );

  /**
   * The two conference captions, drawn down the left edge.
   *
   * ⚠️ Load-bearing for the NFL sheet, not decoration. Drawn as a plain
   * 16-team tree, nothing on the paper says the top half is the AFC, so the
   * sheet reads as a generic bracket that happens to have two byes. The
   * captions cost a left gutter, which is zero on every other page, so no
   * existing sheet moves by a pixel.
   */
  const halfLabels = plan?.halfLabels ?? null;
  const LEFT_GUTTER = halfLabels ? 26 : 0;

  // ---- Page 1: the winners bracket, plus any third-place line ----
  const winnersWidth = LEFT_GUTTER + (layout.rounds.length + 1) * (colW + GUTTER);
  const winnersBottom =
    layout.thirdPlaceCentre !== null
      ? layout.thirdPlaceCentre + layout.slotPitch
      : layout.height;

  const winnersPage: IPagePart = {
    key: 'winners',
    heading: null,
    width: winnersWidth,
    height: winnersBottom,
    render: () => (
      <g transform={`translate(${LEFT_GUTTER}, 0)`}>
        {halfLabels &&
          halfLabels.map((label, h) => {
            // Each half of the draw is exactly half the slots tall.
            const centreY = layout.height / 4 + (h * layout.height) / 2;
            return (
              <text
                key={`half-${h}`}
                transform={`translate(${-LEFT_GUTTER + 10}, ${centreY}) rotate(-90)`}
                fontSize={11}
                fontWeight="700"
                fill={inkAccent}
                textAnchor="middle"
                letterSpacing="0.08em"
                fontFamily="inherit"
              >
                {label.toUpperCase()}
              </text>
            );
          })}

        {layout.rounds.map((round, r) =>
          roundHeader(r * (colW + GUTTER), round.name, `h-${r}`)
        )}
        {roundHeader(
          layout.rounds.length * (colW + GUTTER),
          plan?.championLabel ?? 'Champion',
          'h-champ'
        )}

        {layout.rounds.map((round, r) =>
          round.matches.map((match, m) =>
            renderMatch(
              r * (colW + GUTTER),
              match.lineYs,
              match.centre,
              match.slots,
              `m-${r}-${m}`,
              // Every winners match feeds forward, the final into the champion line.
              true
            )
          )
        )}

        {/* The champion line: one blank line, nothing feeds out of it. */}
        <line
          x1={layout.rounds.length * (colW + GUTTER)}
          y1={layout.championCentre}
          x2={layout.rounds.length * (colW + GUTTER) + colW}
          y2={layout.championCentre}
          stroke={ink}
          strokeWidth={2}
        />

        {layout.thirdPlaceCentre !== null && (
          <g>
            <text
              x={0}
              y={layout.thirdPlaceCentre - layout.slotPitch / 2 - 8}
              fontSize={10}
              fontWeight="700"
              fill={inkAccent}
              letterSpacing="0.06em"
              fontFamily="inherit"
            >
              THIRD PLACE
            </text>
            {renderMatch(
              0,
              [
                layout.thirdPlaceCentre - layout.slotPitch / 2,
                layout.thirdPlaceCentre + layout.slotPitch / 2,
              ],
              layout.thirdPlaceCentre,
              [
                { label: '', isBye: false, seed: null },
                { label: '', isBye: false, seed: null },
              ],
              'third',
              false
            )}
          </g>
        )}
      </g>
    ),
  };

  // ---- Page 2: the blank secondary bracket, when the format has one ----
  const secondaryName =
    format === 'consolation'
      ? 'Consolation bracket'
      : format === 'triple'
        ? 'Second and third chance'
        : 'Losers bracket';

  const losersPage: IPagePart | null =
    layout.losersRounds.length
      ? {
          key: 'losers',
          heading: secondaryName,
          width: layout.losersRounds.length * (colW + GUTTER),
          height: layout.losersHeight,
          render: () => (
            <>
              {/*
                ⚠️ `round.name`, not `L${r + 1}`.
                Building the label from the column index is what made double,
                consolation and triple elimination print three identical-looking
                second sheets. The band above it names the section, so a triple
                sheet visibly separates second chance from third chance.
              */}
              {sectionBands(layout.losersRounds).map((band) => (
                <g key={`band-${band.from}`}>
                  <text
                    x={band.from * (colW + GUTTER)}
                    y={-27}
                    fontSize={9}
                    fontWeight="700"
                    fill={inkAccent}
                    fontFamily="inherit"
                    letterSpacing="0.06em"
                  >
                    {band.label.toUpperCase()}
                  </text>
                  <line
                    x1={band.from * (colW + GUTTER)}
                    y1={-22}
                    x2={band.to * (colW + GUTTER) + colW}
                    y2={-22}
                    stroke={ink}
                    strokeWidth={0.6}
                  />
                </g>
              ))}
              {layout.losersRounds.map((round, r) =>
                roundHeader(r * (colW + GUTTER), round.name, `lh-${r}`)
              )}
              {layout.losersRounds.map((round, r) =>
                round.matches.map((match, m) =>
                  renderMatch(
                    r * (colW + GUTTER),
                    match.lineYs,
                    match.centre,
                    match.slots,
                    `lm-${r}-${m}`,
                    // No inter-round stubs: see the note in bracketMath.
                    false
                  )
                )
              )}
            </>
          ),
        }
      : null;

  /**
   * ---- The mirrored sheet ----
   *
   * Two halves facing each other with the title line between them. The left
   * half is drawn exactly as any other sheet is; the right half is the same
   * geometry with `dir` flipped, so its columns run from the outside of the
   * page inward and its names are written from the right-hand end of each line.
   *
   * ⚠️ The column at index r on the right is NOT at `r * step`. It is at
   * `(columns - 1 - r) * step`, because r counts rounds from the outside in
   * while x counts from the left edge. Getting that backwards draws a sheet
   * that looks almost right and pairs the wrong teams.
   */
  const mirroredPage: IPagePart | null = mirrored
    ? (() => {
        const step = colW + GUTTER;
        const lastCol = mirrored.columns - 1;
        // Left captions sit in a gutter before the sheet; right captions in one
        // after it, so neither can land on top of a name.
        const leftX = (r: number) => r * step;
        const rightX = (r: number) => (lastCol - r) * step;

        const regionCaption = (
          label: string,
          x: number,
          y: number,
          key: string
        ) => (
          <text
            key={key}
            transform={`translate(${x}, ${y}) rotate(-90)`}
            fontSize={11}
            fontWeight="700"
            fill={inkAccent}
            textAnchor="middle"
            letterSpacing="0.08em"
            fontFamily="inherit"
          >
            {label.toUpperCase()}
          </text>
        );

        return {
          key: 'mirrored',
          heading: null,
          width: mirrored.width + REGION_GUTTER * 2,
          height: mirrored.height,
          render: () => (
            <g transform={`translate(${REGION_GUTTER}, 0)`}>
              {mirroredPlan?.regionLabels.map((label, i) =>
                regionCaption(
                  label,
                  // The first two captions belong to the left half, the last
                  // two to the right half. Both sit in the MIDDLE of their
                  // gutter: offsetting them by a fixed amount from the drawing
                  // instead put the right-hand pair closer to the names than
                  // the left-hand pair, which reads as a wonky sheet.
                  i < 2
                    ? -REGION_GUTTER / 2
                    : mirrored.width + REGION_GUTTER / 2,
                  mirrored.regionCentres[i],
                  `region-${i}`
                )
              )}

              {mirrored.left.map((round, r) =>
                roundHeader(leftX(r), round.name, `mlh-${r}`)
              )}
              {mirrored.right.map((round, r) =>
                roundHeader(rightX(r), round.name, `mrh-${r}`)
              )}

              {mirrored.left.map((round, r) =>
                round.matches.map((match, m) =>
                  renderMatch(
                    leftX(r),
                    match.lineYs,
                    match.centre,
                    match.slots,
                    `ml-${r}-${m}`,
                    true,
                    1
                  )
                )
              )}
              {mirrored.right.map((round, r) =>
                round.matches.map((match, m) =>
                  renderMatch(
                    rightX(r),
                    match.lineYs,
                    match.centre,
                    match.slots,
                    `mr-${r}-${m}`,
                    true,
                    -1
                  )
                )
              )}

              {/*
                The champion line. Both halves finish at the same height, so the
                two feed stubs arrive on this one line from opposite sides. That
                is the whole trick of the layout and the reason nothing has to be
                bent around anything else.
              */}
              <text
                x={mirrored.centreX + colW / 2}
                y={mirrored.championCentre - 10}
                fontSize={11}
                fontWeight="700"
                fill={inkAccent}
                textAnchor="middle"
                letterSpacing="0.06em"
                fontFamily="inherit"
              >
                {(mirroredPlan?.championLabel ?? 'Champion').toUpperCase()}
              </text>
              <line
                x1={mirrored.centreX}
                y1={mirrored.championCentre}
                x2={mirrored.centreX + colW}
                y2={mirrored.championCentre}
                stroke={ink}
                strokeWidth={2}
              />
            </g>
          ),
        };
      })()
    : null;

  const pages = mirroredPage
    ? [mirroredPage]
    : losersPage
      ? [winnersPage, losersPage]
      : [winnersPage];

  return (
    <>
      {pages.map((part, i) => {
        const heading = part.heading ? 18 : 0;
        const contentW = part.width + PAD * 2;
        const contentH = TITLE_BAND + heading + HEADER_H + part.height + PAD * 2;

        return (
          <div className="bracket-page" key={part.key}>
            <svg
              viewBox={`0 0 ${contentW} ${contentH}`}
              /*
               * Both dimensions are handed to the box, and `meet` is what makes
               * the promise hold: the drawing scales down until it fits INSIDE
               * the page on whichever axis runs out first, so it can never
               * overflow onto a second sheet.
               */
              preserveAspectRatio="xMidYMid meet"
              role="img"
              aria-label={
                part.key === 'mirrored'
                  ? `${title || 'Tournament'} bracket, four regions of ${
                      (mirrored?.capacity ?? 64) / 4
                    } meeting in the centre`
                  : part.key === 'winners'
                    ? `${title || 'Tournament'} bracket, ${layout.rounds.length} rounds`
                    : `${title || 'Tournament'} ${secondaryName.toLowerCase()}, ${layout.losersRounds.length} rounds`
              }
              xmlns="http://www.w3.org/2000/svg"
            >
              <rect width={contentW} height={contentH} fill={background} />

              {backgroundImage && (
                <image
                  href={backgroundImage}
                  x={0}
                  y={0}
                  width={contentW}
                  height={contentH}
                  opacity={backgroundStrength}
                  /* `slice` fills the sheet and crops the overhang, which is
                     what a background is expected to do. `meet` would letterbox
                     it and leave bands of bare paper down the sides. */
                  preserveAspectRatio="xMidYMid slice"
                />
              )}

              {logo &&
                (() => {
                  const w = Math.round(contentW * logoScale);
                  const h = Math.round(w / (logoAspect || 1));
                  const x =
                    logoCorner === 'tl' || logoCorner === 'bl'
                      ? PAD
                      : contentW - PAD - w;
                  const y =
                    logoCorner === 'tl' || logoCorner === 'tr'
                      ? PAD
                      : contentH - PAD - h;
                  return (
                    <image
                      href={logo}
                      x={x}
                      y={y}
                      width={w}
                      height={h}
                      preserveAspectRatio="xMidYMid meet"
                    />
                  );
                })()}

              {title.trim() && (
                <text
                  x={contentW / 2}
                  y={26}
                  fontSize={17}
                  fontWeight="700"
                  fill={inkText}
                  textAnchor="middle"
                  fontFamily="inherit"
                >
                  {title}
                  {pages.length > 1
                    ? ` · ${i === 0 ? 'Main draw' : secondaryName}`
                    : ''}
                </text>
              )}

              <g transform={`translate(${PAD}, ${TITLE_BAND + heading + HEADER_H})`}>
                {part.render(0)}
              </g>
            </svg>
          </div>
        );
      })}
    </>
  );
};
